// Customer app: the family's view of their home.
import { brand } from "../lib/brand";
import { t } from "../lib/i18n";
import { Link, match, navigate, route } from "../lib/router";
import { isAdminUser, can, session } from "../lib/session";
import { h, store } from "../lib/ui";
import { AccountPanel } from "../shared/auth";
import { LangSwitch, Wordmark } from "../shared/components";
import { Icon } from "../shared/icons";
import { Dashboard } from "./dashboard";
import { NewRequest, TicketView } from "./tickets";
import { PeoplePage } from "./people";
import { VideosPage } from "./videos";

const KEY = "tw_home";
export const current = store({ homeId: "" });

export function homeId(): string {
  const homes = session.homes;
  if (current.homeId && homes.some((x) => x.id === current.homeId)) return current.homeId;
  let saved = "";
  try {
    saved = localStorage.getItem(KEY) ?? "";
  } catch {
    /* storage unavailable */
  }
  return homes.find((x) => x.id === saved)?.id ?? homes[0]?.id ?? "";
}

export function chooseHome(id: string) {
  try {
    localStorage.setItem(KEY, id);
  } catch {
    /* storage unavailable */
  }
  current.set({ homeId: id });
}

function Top(props: { back?: string; title?: string }) {
  return (
    <header class="cust-top">
      {props.back ? (
        <Link href={props.back} class="back"><Icon name="back" size={20} />{props.title ?? t("common.back")}</Link>
      ) : (
        <Wordmark href="/" />
      )}
      <div class="row" style="gap:8px">
        <LangSwitch />
        {isAdminUser() ? <Link href="/admin" class="icon-btn" title={t("customer.toAdmin")}><Icon name="sliders" size={18} /></Link> : null}
        {can("field.visits") ? <Link href="/field" class="icon-btn" title={t("admin.nav.fieldApp")}><Icon name="route" size={18} /></Link> : null}
        <Link href="/account" class="icon-btn" title={t("account.title")}><Icon name="user" size={20} /></Link>
      </div>
    </header>
  );
}

/** Shown above the dashboard when the person has access to more than one home. */
function HomeSwitcher() {
  const homes = session.homes;
  if (homes.length < 2) return null;
  return (
    <div class="seg" role="group" aria-label={t("customer.switchHome")} style="display:flex;margin-bottom:12px;overflow-x:auto">
      {homes.map((x) => (
        <button aria-pressed={String(x.id === homeId())} style="flex:1;white-space:nowrap" onClick={() => chooseHome(x.id)}>{x.nickname || x.address_line1}</button>
      ))}
    </div>
  );
}

function NoHome() {
  return (
    <div class="empty" style="padding:60px 10px">
      <strong>{t("customer.noHomeTitle")}</strong>
      <span>{t("customer.noHomeBody", { app: brand.current.appName })}</span>
    </div>
  );
}

export function CustomerApp() {
  const p = route.path;
  const id = homeId();
  let m;
  let body: any;
  let top: any = <Top />;
  if (p === "/account") {
    top = <Top back="/" title={t("account.title")} />;
    body = <AccountPanel />;
  } else if (!id) body = <NoHome />;
  else if (p === "/request") {
    top = <Top back="/" title={t("customer.request.title")} />;
    body = <NewRequest homeId={id} />;
  } else if ((m = match("/requests/:id"))) {
    top = <Top back="/" title={t("customer.requests")} />;
    body = <TicketView id={m.id} />;
  } else if (p === "/people") {
    top = <Top back="/" title={t("customer.people.title")} />;
    body = <PeoplePage homeId={id} />;
  } else if (p === "/videos") {
    top = <Top back="/" title={t("customer.videos.title")} />;
    body = <VideosPage homeId={id} />;
  } else if (p === "/") body = [<HomeSwitcher />, <Dashboard homeId={id} />];
  else {
    queueMicrotask(() => navigate("/", { replace: true }));
    body = null;
  }
  return (
    <div class="cust">
      {top}
      {body}
    </div>
  );
}
